import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  getNationalities,
  getGenders,
  createVisaApplication,
  saveApplicantInfo,
  saveTravelInfo,
  uploadVisaDocuments,
  acceptVisaTerms,
  getPreviousApplications,
  calculateVisaFee,
} from "../services/visaService";
import "./VisaApplication.css";
import NavUser from "../components/NavUser";
import SEO from "../components/SEO";
import {
  FaFilePdf,
  FaCheckCircle,
  FaCreditCard,
  FaShieldAlt,
  FaPassport,
  FaPlaneArrival,
  FaGlobeAmericas,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaInfoCircle,
  FaSearch,
  FaCheck,
  FaBuilding,
  FaClock,
} from "react-icons/fa";

const STEP_LABELS = [
  "ETA Overview",
  "Visa Category",
  "Personal Info",
  "Contact Info",
  "Travel Details",
  "Documents",
  "Payment",
];
const TOTAL_STEPS = STEP_LABELS.length - 1;

// The 40 Active Free-Tier Countries ($0 Govt Fee)
const FREE_TIER_40_COUNTRIES = [
  "Australia", "Austria", "Bahrain", "Belarus", "Belgium", "Canada", "China", "Czech Republic",
  "Denmark", "Finland", "France", "Germany", "India", "Indonesia", "Iran", "Israel", "Italy",
  "Japan", "Kazakhstan", "Kuwait", "Malaysia", "Nepal", "Netherlands", "New Zealand", "Norway",
  "Oman", "Pakistan", "Poland", "Qatar", "Russia", "Saudi Arabia", "South Korea", "Spain",
  "Sweden", "Switzerland", "Thailand", "Turkey", "United Arab Emirates", "United Kingdom",
  "United States of America"
];

// Bilateral Exemption Partners
const BILATERAL_COUNTRIES = ["Maldives", "Seychelles", "Singapore"];

// SAARC Regional Agreement Paid Group ($20 Govt Fee)
const SAARC_COUNTRIES = ["Bangladesh", "Bhutan", "Myanmar"];

// Restricted Nationalities (Mandatory Physical Sponsor Route via Battaramulla)
const RESTRICTED_SPONSOR_COUNTRIES = [
  "Cameroon", "Nigeria", "Ivory Coast", "Ghana", "Afghanistan", "Syria", "Taiwan-China", "Kosovo"
];

// Full global nationality list for instant fallback & dropdown completeness
const COMPREHENSIVE_NATIONALITIES = [
  // 40 Free-tier + Bilateral Partners
  "Australia", "Austria", "Bahrain", "Belarus", "Belgium", "Canada", "China", "Czech Republic",
  "Denmark", "Finland", "France", "Germany", "India", "Indonesia", "Iran", "Israel", "Italy",
  "Japan", "Kazakhstan", "Kuwait", "Malaysia", "Maldives", "Nepal", "Netherlands", "New Zealand",
  "Norway", "Oman", "Pakistan", "Poland", "Qatar", "Russia", "Saudi Arabia", "Seychelles",
  "Singapore", "South Korea", "Spain", "Sweden", "Switzerland", "Thailand", "Turkey",
  "United Arab Emirates", "United Kingdom", "United States of America", "USA",
  
  // SAARC Paid Group
  "Bangladesh", "Bhutan", "Myanmar",

  // Restricted Sponsor Track
  "Afghanistan", "Cameroon", "Ghana", "Ivory Coast", "Kosovo", "Nigeria", "Syria", "Taiwan-China",

  // Global Nationalities
  "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Azerbaijan", "Bahamas",
  "Barbados", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria",
  "Cambodia", "Chile", "Colombia", "Costa Rica", "Croatia", "Cyprus", "Czechia", "Dominican Republic",
  "Ecuador", "Egypt", "Estonia", "Ethiopia", "Fiji", "Georgia", "Greece", "Guatemala", "Honduras",
  "Hong Kong", "Hungary", "Iceland", "Ireland", "Jamaica", "Jordan", "Kenya", "Latvia",
  "Lebanon", "Liechtenstein", "Lithuania", "Luxembourg", "Malta", "Mauritius", "Mexico", "Monaco",
  "Mongolia", "Montenegro", "Morocco", "Namibia", "New Caledonia", "North Macedonia", "Panama",
  "Paraguay", "Peru", "Philippines", "Portugal", "Romania", "San Marino", "Serbia", "Slovakia",
  "Slovenia", "South Africa", "Sri Lanka", "Tanzania", "Trinidad and Tobago", "Tunisia", "Uganda",
  "Ukraine", "Uruguay", "Uzbekistan", "Vatican City", "Vietnam", "Zambia", "Zimbabwe",

  // Demonyms
  "American", "British", "Sri Lankan", "Australian", "Canadian", "Chinese", "French", "German", "Indian", "Japanese"
];

function VisaApplication() {
  const [step, setStep] = useState(0);
  const [nationalities, setNationalities] = useState(
    COMPREHENSIVE_NATIONALITIES.map((name, idx) => ({ id: idx + 1, name }))
  );
  const [genders, setGenders] = useState([]);
  const [applicationId, setApplicationId] = useState(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [previousApplications, setPreviousApplications] = useState([]);
  const [step2Errors, setStep2Errors] = useState({});

  // Active sub-tab in Step 0
  const [infoTab, setInfoTab] = useState("overview"); // "overview" | "free-list" | "extensions" | "sponsor-route"
  const [countryFilter, setCountryFilter] = useState("");

  const [formData, setFormData] = useState({
    eligibleCountry: "",
    visaType: "1",
    fullName: "",
    dob: "",
    nationality: "",
    gender: "",
    passportNumber: "",
    passportIssue: "",
    passportExpiry: "",
    email: "",
    phone: "",
    emergencyName: "",
    emergencyPhone: "",
    purpose: "",
    departureDate: "",
    returnDate: "",
    destinations: "",
    accommodation: "",
    flightNumber: "",
    sponsorName: "",
    sponsorNic: "",
    sponsorPhone: "",
    sponsorAddress: "",
    uploadedFiles: [],
    acceptTerms: false,
    paymentMethod: "payments_lk",
  });

  // Dynamic pricing calculation
  const [pricingInfo, setPricingInfo] = useState({
    govt_fee_usd: 0,
    service_fee_usd: 10,
    total_fee_usd: 10,
    total_fee_lkr: 3000,
    is_free_tier: true,
    is_saarc: false,
    is_restricted_sponsor: false,
    category_name: "Tourist ETA (Free Tier)",
    standard_duration: "30 Days",
    entry_type: "Double-Entry",
    conditions: "Double-entry short stay tourism. 40 Free-Tier countries pay $0 Govt fee.",
    required_documents: ["Passport Bio Page (min 6 months validity)"],
  });

  // Load Nationalities and Genders
  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [nats, gens] = await Promise.all([
          getNationalities(),
          getGenders(),
        ]);
        
        // Merge API nationalities with client-side comprehensive list to guarantee all 115+ are present
        const fetchedList = Array.isArray(nats) ? nats : [];
        const natMap = new Map();
        
        // Add fetched from DB first
        fetchedList.forEach((n) => {
          if (n && n.name) {
            natMap.set(n.name.toLowerCase().trim(), { id: n.id, name: n.name });
          }
        });

        // Add comprehensive list to ensure full catalog is always available
        COMPREHENSIVE_NATIONALITIES.forEach((name) => {
          const key = name.toLowerCase().trim();
          if (!natMap.has(key)) {
            natMap.set(key, { id: name, name });
          }
        });

        const mergedNats = Array.from(natMap.values()).sort((a, b) =>
          a.name.localeCompare(b.name)
        );

        setNationalities(mergedNats);
        setGenders(gens || []);
      } catch (err) {
        console.error("Failed to load initial metadata:", err);
      }
    };
    fetchMeta();
  }, []);

  // Compute pricing when nationality or visa type changes
  useEffect(() => {
    const computePricing = async () => {
      let natName = formData.eligibleCountry || "";
      if (!natName && formData.nationality) {
        const match = nationalities.find(
          (n) => String(n.id) === String(formData.nationality) || n.name.toLowerCase() === String(formData.nationality).toLowerCase()
        );
        if (match) natName = match.name;
      }

      try {
        const data = await calculateVisaFee({
          nationality_name: natName,
          visa_type_id: Number(formData.visaType || 1),
        });
        if (data && data.pricing) {
          setPricingInfo(data.pricing);
        }
      } catch (e) {
        // Fallback calculation client-side
        const vId = Number(formData.visaType || 1);
        const norm = (natName || "").toLowerCase();
        const isFree = FREE_TIER_40_COUNTRIES.some((c) => c.toLowerCase() === norm) ||
          BILATERAL_COUNTRIES.some((c) => c.toLowerCase() === norm);
        const isSaarc = SAARC_COUNTRIES.some((c) => c.toLowerCase() === norm);
        const isRestricted = RESTRICTED_SPONSOR_COUNTRIES.some((c) => c.toLowerCase() === norm);

        let govt = 50;
        let cat = "Tourist ETA (Standard Paid)";
        let dur = "30 Days";
        let entry = "Double-Entry";

        if (vId === 3) {
          govt = 0;
          cat = "Transit ETA";
          dur = "Up to 2 Days (48h)";
          entry = "Single-Entry";
        } else if (vId === 2) {
          govt = 55;
          cat = "Business ETA";
          dur = "30 Days";
          entry = "Double-Entry";
        } else if (isFree || vId === 1) {
          if (isFree) {
            govt = 0;
            cat = "Tourist ETA (Free Tier - 40 Countries & Bilateral)";
            dur = norm === "maldives" ? "90 Days" : "30 Days";
          } else if (isSaarc || vId === 4) {
            govt = 20;
            cat = "Tourist ETA (SAARC Paid Group)";
          }
        }

        const totalUsd = govt + 10;
        setPricingInfo({
          govt_fee_usd: govt,
          service_fee_usd: 10,
          total_fee_usd: totalUsd,
          total_fee_lkr: totalUsd * 300,
          is_free_tier: isFree,
          is_saarc: isSaarc,
          is_restricted_sponsor: isRestricted,
          category_name: cat,
          standard_duration: dur,
          entry_type: entry,
          conditions: isFree
            ? "$0 Govt fee under the active 40-country policy. $10 Soloway processing fee applies."
            : isSaarc
            ? "$20 regional SAARC rate + $10 Soloway processing fee."
            : `$${govt} Govt fee + $10 Soloway processing fee.`,
          required_documents: ["Passport Bio-Data Page"],
        });
      }
    };

    computePricing();
  }, [formData.eligibleCountry, formData.nationality, formData.visaType, nationalities]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setFormData((prev) => ({ ...prev, [name]: Array.from(files) }));
    } else if (e.target.type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: e.target.checked }));
    } else if (name === "eligibleCountry") {
      const match = nationalities.find(
        (n) => n.name.toLowerCase() === value.toLowerCase()
      );
      setFormData((prev) => ({
        ...prev,
        eligibleCountry: value,
        nationality: match ? String(match.id) : value,
      }));
    } else if (name === "nationality") {
      const match = nationalities.find(
        (n) => String(n.id) === String(value) || n.name.toLowerCase() === String(value).toLowerCase()
      );
      setFormData((prev) => ({
        ...prev,
        nationality: value,
        eligibleCountry: match ? match.name : value,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleNext = () => setStep((prev) => prev + 1);
  const handleBack = () => step > 0 && setStep((prev) => prev - 1);

  // Filtered 40-country list
  const filtered40Countries = useMemo(() => {
    if (!countryFilter.trim()) return FREE_TIER_40_COUNTRIES;
    return FREE_TIER_40_COUNTRIES.filter((c) =>
      c.toLowerCase().includes(countryFilter.toLowerCase())
    );
  }, [countryFilter]);

  // Step 0 -> Start Application with country selection
  const handleStartFromOverview = (countryName = "") => {
    const selected = countryName || formData.eligibleCountry;
    if (selected) {
      const matchNat = nationalities.find(
        (n) => n.name.toLowerCase() === selected.toLowerCase()
      );
      setFormData((prev) => ({
        ...prev,
        eligibleCountry: selected,
        nationality: matchNat ? String(matchNat.id) : selected,
      }));
    }
    setStep(1);
  };

  // Step 1: Submit initial visa application
  const handleStep1Next = async () => {
    try {
      const payload = {
        visa_type_id: Number(formData.visaType || 1),
      };

      const res = await createVisaApplication(payload);
      setApplicationId(res.application.id);
      handleNext();
    } catch (err) {
      console.error(err);
      alert("Failed to start visa application. Please verify connection.");
    }
  };

  const validateStep2Dates = () => {
    const errors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dob = formData.dob ? new Date(formData.dob) : null;
    const issue = formData.passportIssue ? new Date(formData.passportIssue) : null;
    const expiry = formData.passportExpiry ? new Date(formData.passportExpiry) : null;

    if (!formData.fullName?.trim()) {
      errors.fullName = "Full name as in passport is required";
    }
    if (!formData.nationality) {
      errors.nationality = "Nationality is required";
    }
    if (!formData.gender) {
      errors.gender = "Gender is required";
    }
    if (!formData.passportNumber?.trim()) {
      errors.passportNumber = "Passport number is required";
    }
    if (!dob) {
      errors.dob = "Date of birth is required";
    } else if (dob >= today) {
      errors.dob = "Date of birth must be in the past";
    }
    if (!issue) {
      errors.passportIssue = "Passport issue date is required";
    } else if (issue > today) {
      errors.passportIssue = "Issue date cannot be in the future";
    }
    const minExpiryDate = new Date(today);
    minExpiryDate.setMonth(minExpiryDate.getMonth() + 6);

    if (!expiry) {
      errors.passportExpiry = "Passport expiry date is required";
    } else if (expiry <= today) {
      errors.passportExpiry = "Expiry date must be in the future (min 6 months validity)";
    } else if (expiry < minExpiryDate) {
      errors.passportExpiry = "Passport must have at least 6 months validity from current date";
    } else if (issue && expiry <= issue) {
      errors.passportExpiry = "Expiry date must be after issue date";
    }

    setStep2Errors(errors);
    return Object.keys(errors).length === 0;
  };

  // Step 2 Submit
  const handleStep2Submit = async () => {
    if (!validateStep2Dates()) return;
    if (!applicationId) return alert("Application session missing. Please restart.");

    const payload = {
      full_name: formData.fullName,
      passport_full_name: formData.fullName,
      passport_number: formData.passportNumber,
      passport_issue_date: formData.passportIssue,
      passport_expiry_date: formData.passportExpiry,
      dob: formData.dob,
      nationality_id: !isNaN(Number(formData.nationality)) && Number(formData.nationality) > 0 ? Number(formData.nationality) : (formData.nationality || formData.eligibleCountry),
      gender_id: Number(formData.gender),
    };

    try {
      await saveApplicantInfo(applicationId, payload);
      setStep(3);
    } catch (err) {
      console.error("Step 2 submission error:", err.response?.data || err);
      alert("Failed to save personal info.");
    }
  };

  // Step 3 Submit (Contact Info)
  const handleStep3Submit = async () => {
    if (!applicationId) return alert("Application ID missing");

    const payload = {
      full_name: formData.fullName,
      passport_full_name: formData.fullName,
      passport_number: formData.passportNumber,
      passport_issue_date: formData.passportIssue,
      passport_expiry_date: formData.passportExpiry,
      email: formData.email || "",
      phone_number: formData.phone || "",
      dob: formData.dob,
      nationality_id: !isNaN(Number(formData.nationality)) && Number(formData.nationality) > 0 ? Number(formData.nationality) : (formData.nationality || formData.eligibleCountry),
      gender_id: Number(formData.gender),
      emergency_contact_name: formData.emergencyName || "",
      emergency_contact: formData.emergencyPhone || "",
    };

    try {
      await saveApplicantInfo(applicationId, payload);
      setStep(4);
    } catch (err) {
      console.error("Step 3 submission error:", err.response?.data || err);
      alert("Failed to save contact info.");
    }
  };

  // Step 4 Submit (Travel Info + Sponsor info if restricted)
  const handleStep4Submit = async () => {
    let accommodation = formData.accommodation || "";
    if (pricingInfo.is_restricted_sponsor && formData.sponsorName) {
      accommodation += ` | Local Sponsor: ${formData.sponsorName} (NIC/Passport: ${formData.sponsorNic || "N/A"}, Phone: ${formData.sponsorPhone || "N/A"}, Address: ${formData.sponsorAddress || "N/A"})`;
    }

    const payload = {
      travel_purpose: formData.purpose || pricingInfo.category_name,
      planned_travel_start: formData.departureDate || null,
      planned_travel_end: formData.returnDate || null,
      planned_arrival_date: formData.departureDate || null,
      duration_of_stay:
        formData.returnDate && formData.departureDate
          ? Math.ceil(
              (new Date(formData.returnDate) - new Date(formData.departureDate)) /
                (1000 * 60 * 60 * 24)
            )
          : null,
      destination: formData.destinations || "Sri Lanka",
      accommodation_details: accommodation,
      flight_number: formData.flightNumber || "",
    };

    try {
      await saveTravelInfo(applicationId, payload);
      setStep(5);
    } catch (err) {
      console.error("Step 4 submission error:", err.response?.data || err);
      alert("Failed to save travel info.");
    }
  };

  // Step 5 Document Upload
  const handleDocumentUploadAndAcceptTerms = async () => {
    if (!applicationId) return alert("Application ID missing");
    if (!acceptTerms) return alert("You must accept the ETA Terms & Immigration Declaration");
    if (!formData.uploadedFiles?.length)
      return alert("Please upload at least your Passport Bio-Data Page");

    try {
      const formDataObj = new FormData();
      formData.uploadedFiles.forEach((file) => formDataObj.append("visa_doc", file));

      await uploadVisaDocuments(applicationId, formDataObj);
      await acceptVisaTerms(applicationId);
      setStep(6);
    } catch (err) {
      console.error("Document upload error:", err.response?.data || err);
      alert("Failed to upload documents or accept terms.");
    }
  };

  // Step 6 Payment (Payments.lk Hosted Checkout)
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  const handlePaymentsLkVisaPayment = async () => {
    if (!applicationId) return alert("Application ID missing");
    try {
      setIsProcessingPayment(true);
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/payment/payments-lk/create-checkout`,
        {
          bookingType: "visa",
          bookingId: applicationId,
          clientType: "web",
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("jwt_token")}` },
        }
      );
      if (res.data?.checkoutUrl) {
        window.location.href = res.data.checkoutUrl;
      } else {
        alert("Failed to initiate Payments.lk secure checkout.");
        setIsProcessingPayment(false);
      }
    } catch (err) {
      console.error("Payments.lk Visa Checkout error:", err);
      alert(err.response?.data?.error || "Error initiating Payments.lk checkout");
      setIsProcessingPayment(false);
    }
  };


  // Load previous applications on Step 0
  useEffect(() => {
    if (step !== 0) return;
    const fetchApplications = async () => {
      try {
        const data = await getPreviousApplications();
        setPreviousApplications(data.applications || []);
      } catch (err) {
        console.error("Failed to load applications", err);
      }
    };
    fetchApplications();
  }, [step]);

  return (
    <div className="visa-app-container">
      <SEO
        title="Sri Lanka Electronic Travel Authorization (ETA) - Official Assistance"
        description="Apply online for your official Sri Lanka ETA Tourist, Business, or Transit Visa. Pre-departure processing, 40 free-tier countries verification, transparent $10 service fee."
        canonicalPath="/visa-application"
      />
      <NavUser />
      
      {/* Header Banner */}
      <div className="visa-header-section">
        <div className="visa-official-badge">
          <FaShieldAlt className="shield-icon" /> Official ETA Entry Portal & Verification
        </div>
        <h1 className="visa-app-title">Sri Lanka Electronic Travel Authorization (ETA)</h1>
        <p className="visa-app-subtitle">
          Mandatory pre-departure travel authorization managed under the Department of Immigration and Emigration (eta.gov.lk).
          Valid across all international airports and designated seaports in Sri Lanka.
        </p>
      </div>

      {/* Stepper */}
      {step <= TOTAL_STEPS && (
        <div className="visa-app-stepper">
          <div className="visa-stepper-track"></div>
          <div
            className="visa-stepper-progress"
            style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
          />
          {STEP_LABELS.map((label, index) => (
            <div
              key={index}
              className={`visa-step ${
                step === index
                  ? "visa-step-active"
                  : step > index
                  ? "visa-step-complete"
                  : ""
              }`}
            >
              <span className="visa-step-index">
                {step > index ? <FaCheck size={14} /> : index + 1}
              </span>
              <span className="visa-step-label">{label}</span>
            </div>
          ))}
        </div>
      )}

      <div className="visa-app-content">
        {/* ─── STEP 0: ETA Architecture & Information Hub ────────────────────────── */}
        {step === 0 && (
          <div className="visa-info-step">
            {/* Navigation Tabs for Step 0 */}
            <div className="eta-policy-tabs">
              <button
                className={`eta-tab-btn ${infoTab === "overview" ? "active" : ""}`}
                onClick={() => setInfoTab("overview")}
              >
                <FaInfoCircle /> ETA Policy & Architecture
              </button>
              <button
                className={`eta-tab-btn ${infoTab === "free-list" ? "active" : ""}`}
                onClick={() => setInfoTab("free-list")}
              >
                <FaGlobeAmericas /> 40 Free-Tier Countries ($0 Govt Fee)
              </button>
              <button
                className={`eta-tab-btn ${infoTab === "extensions" ? "active" : ""}`}
                onClick={() => setInfoTab("extensions")}
              >
                <FaCalendarAlt /> Post-Arrival Extensions (270 Days)
              </button>
              <button
                className={`eta-tab-btn ${infoTab === "sponsor-route" ? "active" : ""}`}
                onClick={() => setInfoTab("sponsor-route")}
              >
                <FaBuilding /> Restricted Nationalities (Sponsor Track)
              </button>
            </div>

            {/* TAB 1: OVERVIEW */}
            {infoTab === "overview" && (
              <div className="eta-tab-pane">
                <div className="visa-process-grid">
                  <div className="visa-process-card highlight-card">
                    <span className="visa-process-step"><FaPlaneArrival /></span>
                    <h4>1. Mandatory Pre-Departure ETA</h4>
                    <p>
                      <strong>Visa on arrival has been completely replaced</strong> by mandatory pre-departure online applications.
                      Every foreign traveler must obtain an approved ETA before boarding their flight.
                    </p>
                  </div>

                  <div className="visa-process-card">
                    <span className="visa-process-step"><FaGlobeAmericas /></span>
                    <h4>2. 40 Free-Tier Countries</h4>
                    <p>
                      Nationals of <strong>40 designated countries</strong> + Bilateral partners (Maldives 90 days, Seychelles, Singapore) receive <strong>$0 Government Fee</strong>.
                      Our verification charge is a flat <strong>$10.00 USD</strong>.
                    </p>
                  </div>

                  <div className="visa-process-card">
                    <span className="visa-process-step"><FaClock /></span>
                    <h4>3. 24-Hour Processing</h4>
                    <p>
                      Fast, automated processing directly synced with Sri Lanka Immigration. We recommend applying at least 3 business days before departure.
                    </p>
                  </div>

                  <div className="visa-process-card">
                    <span className="visa-process-step"><FaCreditCard /></span>
                    <h4>4. Transparent Pricing</h4>
                    <p>
                      Government fee ($0 Free / $20 SAARC / $50 Standard / $55 Business) + <strong>$10 Soloway Verification Fee</strong>. No hidden surcharges.
                    </p>
                  </div>
                </div>

                {/* Country Quick Check Bar */}
                <div className="visa-eligibility-select">
                  <div className="visa-eligibility-header">
                    <h4>Check Your Nationality's ETA Rate & Category</h4>
                    <p>Select your passport country below to view exact pricing and entry requirements:</p>
                  </div>
                  <div className="eligibility-input-group">
                    <select
                      name="eligibleCountry"
                      value={formData.eligibleCountry}
                      onChange={handleChange}
                      className="visa-country-select"
                    >
                      <option value="">-- Choose Nationality / Country --</option>
                      {nationalities.map((n) => (
                        <option key={n.id} value={n.name}>
                          {n.name}
                        </option>
                      ))}
                    </select>

                    <button
                      className="visa-btn apply-btn-direct"
                      onClick={() => handleStartFromOverview()}
                      disabled={!formData.eligibleCountry}
                    >
                      Apply for ETA Visa →
                    </button>
                  </div>

                  {formData.eligibleCountry && (
                    <div className={`eligibility-result-banner ${pricingInfo.is_free_tier ? "banner-free" : pricingInfo.is_restricted_sponsor ? "banner-restricted" : "banner-standard"}`}>
                      <div className="banner-left">
                        <span className="banner-title">{formData.eligibleCountry}</span>
                        <span className="banner-desc">
                          {pricingInfo.category_name} | {pricingInfo.standard_duration} ({pricingInfo.entry_type})
                        </span>
                      </div>
                      <div className="banner-right">
                        <div className="fee-breakdown-mini">
                          <span>Govt Fee: <strong>${pricingInfo.govt_fee_usd} USD</strong></span>
                          <span>Soloway Charge: <strong>$10 USD</strong></span>
                        </div>
                        <div className="total-badge">
                          Total: ${pricingInfo.total_fee_usd} USD (Rs. {pricingInfo.total_fee_lkr.toLocaleString()})
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* TAB 2: 40 FREE-TIER COUNTRIES */}
            {infoTab === "free-list" && (
              <div className="eta-tab-pane">
                <div className="free-tier-header">
                  <h3>The 40 Active Free-Tier Countries & Bilateral Partners ($0 Govt Fee)</h3>
                  <p>
                    Holders of ordinary, diplomatic, or official passports from these 40 countries process their 30-day tourist ETAs at <strong>USD 0 Government Fee</strong> (Plus bilateral agreements for Maldives, Seychelles, and Singapore. Maldivians receive 90 days validity):
                  </p>
                </div>

                <div className="country-search-bar">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search 40 free countries (e.g. United Kingdom, Germany, Japan)..."
                    value={countryFilter}
                    onChange={(e) => setCountryFilter(e.target.value)}
                  />
                </div>

                <div className="country-chips-grid">
                  {filtered40Countries.map((country, idx) => (
                    <div
                      key={idx}
                      className="country-chip clickable"
                      onClick={() => handleStartFromOverview(country)}
                      title="Click to apply with this country"
                    >
                      <span className="country-chip-num">{idx + 1}</span>
                      <span className="country-chip-name">{country}</span>
                      <span className="country-chip-badge">$0 Govt</span>
                    </div>
                  ))}
                </div>

                <div className="bilateral-partners-box">
                  <h4>Bilateral Agreement Exemptions:</h4>
                  <div className="bilateral-chips">
                    <span className="b-chip">🇲🇻 Maldives (90 Days - $0 Govt Fee)</span>
                    <span className="b-chip">🇸🇨 Seychelles (30 Days - $0 Govt Fee)</span>
                    <span className="b-chip">🇸🇬 Singapore (30 Days - $0 Govt Fee)</span>
                  </div>
                </div>

                <div className="action-center">
                  <button
                    className="visa-btn"
                    onClick={() => handleStartFromOverview()}
                  >
                    Start Free-Tier Application ($10 Soloway Verification) →
                  </button>
                </div>
              </div>
            )}

            {/* TAB 3: EXTENSIONS */}
            {infoTab === "extensions" && (
              <div className="eta-tab-pane">
                <div className="extension-guide-header">
                  <h3>Post-Arrival Stay Extension Guidelines (Up to 270 Days)</h3>
                  <p>
                    If tourists wish to extend their stay beyond the initial 30-day entry, extensions can be processed via the Department of Immigration & Emigration (Colombo) across three incremental tiers:
                  </p>
                </div>

                <div className="extension-table-card">
                  <table className="eta-custom-table">
                    <thead>
                      <tr>
                        <th>Extension Tier</th>
                        <th>Duration Added</th>
                        <th>Cumulative Total Stay</th>
                        <th>Approximate Govt Fee</th>
                        <th>Processing Location</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>First Extension</strong></td>
                        <td>Up to 60 additional days</td>
                        <td>Up to 90 Days total</td>
                        <td><span className="table-price">~USD 50</span></td>
                        <td>Dept of Immigration (Colombo) / Online portal</td>
                      </tr>
                      <tr>
                        <td><strong>Second Extension</strong></td>
                        <td>Up to further 90 days</td>
                        <td>Up to 180 Days total</td>
                        <td><span className="table-price">~USD 75</span></td>
                        <td>Dept of Immigration (Colombo)</td>
                      </tr>
                      <tr>
                        <td><strong>Third Extension</strong></td>
                        <td>Up to final 90 days</td>
                        <td>Up to 270 Days (Maximum Cap)</td>
                        <td><span className="table-price">~USD 100</span></td>
                        <td>Dept of Immigration Head Office</td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="extension-note">
                    <FaInfoCircle /> <strong>Maximum Cumulative Stay Cap:</strong> 270 days total from the initial entry date.
                  </div>
                </div>

                <div className="action-center">
                  <button className="visa-btn" onClick={() => handleStartFromOverview()}>
                    Apply for Initial 30-Day ETA →
                  </button>
                </div>
              </div>
            )}

            {/* TAB 4: RESTRICTED SPONSOR ROUTE */}
            {infoTab === "sponsor-route" && (
              <div className="eta-tab-pane">
                <div className="sponsor-route-alert">
                  <FaExclamationTriangle className="alert-big-icon" />
                  <div>
                    <h4>Special Stream Compliance: Restricted Nationalities Track</h4>
                    <p>
                      Certain nationalities cannot use the direct automated ETA gate without physical Sri Lankan sponsor routing via the Head Office of the Department of Immigration and Emigration (Battaramulla).
                    </p>
                  </div>
                </div>

                <div className="restricted-countries-box">
                  <h4>Restricted Nationalities:</h4>
                  <div className="restricted-tags">
                    {RESTRICTED_SPONSOR_COUNTRIES.map((c, i) => (
                      <span key={i} className="restricted-tag">{c}</span>
                    ))}
                  </div>
                </div>

                <div className="documents-checklist-card">
                  <h4>Mandatory Documentation Checklist for Sponsored Track:</h4>
                  <ul>
                    <li>1. Local Sri Lankan sponsor request letter</li>
                    <li>2. Official affidavit signed before a Justice of the Peace (JP) or Lawyer</li>
                    <li>3. Copy of sponsor's National Identity Card (NIC) or Passport</li>
                    <li>4. Applicant's original police clearance report</li>
                    <li>5. Valid passport bio-data photocopy (minimum 6 months validity)</li>
                    <li>6. Confirmed proof of hotel accommodation / host address</li>
                  </ul>
                </div>

                <div className="action-center">
                  <button className="visa-btn" onClick={() => handleStartFromOverview()}>
                    Proceed with Verified Application →
                  </button>
                </div>
              </div>
            )}

            {/* Previous Applications List */}
            {previousApplications.length > 0 && (
              <div className="previous-visa-list">
                <h3>Your Previous Applications</h3>
                <div className="visa-table">
                  {previousApplications.map((app, index) => (
                    <div key={index} className="visa-row">
                      <span className="visa-col id-col">{app.id}</span>
                      <span className="visa-col date-col">
                        <p>
                          <strong>Submitted:</strong> {new Date(app.submitted_at).toLocaleDateString()}
                        </p>
                      </span>
                      <span
                        className={`visa-status-badge ${
                          app.visa_status === "Approved"
                            ? "status-approved"
                            : "status-pending"
                        }`}
                      >
                        {app.visa_status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ─── STEP 1: Select Visa Category ──────────────────────────────────────── */}
        {step === 1 && (
          <div className="visa-visa-type-step">
            <h3>Select Your ETA Visa Category</h3>
            <p className="step-subtitle">
              Choose the appropriate category. All categories include our verified pre-departure compliance and $10 Soloway service fee.
            </p>

            <div className="visa-categories-grid">
              {/* Category 1: Tourist ETA */}
              <div
                className={`visa-category-card ${formData.visaType === "1" ? "selected" : ""}`}
                onClick={() => setFormData({ ...formData, visaType: "1" })}
              >
                <div className="card-top">
                  <span className="cat-badge">Double-Entry</span>
                  <span className="cat-duration">30 Days (Maldives: 90d)</span>
                </div>
                <h4>Tourist ETA</h4>
                <p className="cat-desc">
                  For tourism, holidays, visiting friends & relatives, and leisure activities.
                </p>
                <div className="cat-pricing">
                  <div className="price-line">
                    <span>Govt Fee:</span>
                    <strong>{pricingInfo.is_free_tier ? "$0 (40-Free List)" : "$50 USD"}</strong>
                  </div>
                  <div className="price-line">
                    <span>Soloway Charge:</span>
                    <strong>$10 USD</strong>
                  </div>
                  <div className="price-total">
                    <span>Total Payable:</span>
                    <strong>${pricingInfo.is_free_tier ? "10.00" : "60.00"} USD</strong>
                  </div>
                </div>
              </div>

              {/* Category 2: Business ETA */}
              <div
                className={`visa-category-card ${formData.visaType === "2" ? "selected" : ""}`}
                onClick={() => setFormData({ ...formData, visaType: "2" })}
              >
                <div className="card-top">
                  <span className="cat-badge commercial">Double-Entry</span>
                  <span className="cat-duration">30 Days</span>
                </div>
                <h4>Business ETA</h4>
                <p className="cat-desc">
                  For corporate meetings, conferences, business negotiations, and seminars. Prohibits employment.
                </p>
                <div className="cat-pricing">
                  <div className="price-line">
                    <span>Govt Fee:</span>
                    <strong>$55 USD</strong>
                  </div>
                  <div className="price-line">
                    <span>Soloway Charge:</span>
                    <strong>$10 USD</strong>
                  </div>
                  <div className="price-total">
                    <span>Total Payable:</span>
                    <strong>$65.00 USD</strong>
                  </div>
                </div>
              </div>

              {/* Category 3: Transit ETA */}
              <div
                className={`visa-category-card ${formData.visaType === "3" ? "selected" : ""}`}
                onClick={() => setFormData({ ...formData, visaType: "3" })}
              >
                <div className="card-top">
                  <span className="cat-badge transit">Single-Entry</span>
                  <span className="cat-duration">Up to 48 Hours</span>
                </div>
                <h4>Transit ETA</h4>
                <p className="cat-desc">
                  For travelers transiting outside airport terminal with confirmed onward ticket.
                </p>
                <div className="cat-pricing">
                  <div className="price-line">
                    <span>Govt Fee:</span>
                    <strong>$0 (Free)</strong>
                  </div>
                  <div className="price-line">
                    <span>Soloway Charge:</span>
                    <strong>$10 USD</strong>
                  </div>
                  <div className="price-total">
                    <span>Total Payable:</span>
                    <strong>$10.00 USD</strong>
                  </div>
                </div>
              </div>

              {/* Category 4: SAARC Regional Group */}
              <div
                className={`visa-category-card ${formData.visaType === "4" ? "selected" : ""}`}
                onClick={() => setFormData({ ...formData, visaType: "4" })}
              >
                <div className="card-top">
                  <span className="cat-badge saarc">Double-Entry</span>
                  <span className="cat-duration">30 Days</span>
                </div>
                <h4>SAARC Regional Tourist ETA</h4>
                <p className="cat-desc">
                  Regional agreement discount for nationals of Bangladesh, Bhutan, Myanmar.
                </p>
                <div className="cat-pricing">
                  <div className="price-line">
                    <span>Govt Fee:</span>
                    <strong>$20 USD</strong>
                  </div>
                  <div className="price-line">
                    <span>Soloway Charge:</span>
                    <strong>$10 USD</strong>
                  </div>
                  <div className="price-total">
                    <span>Total Payable:</span>
                    <strong>$30.00 USD</strong>
                  </div>
                </div>
              </div>
            </div>

            <div className="visa-nav-buttons">
              <button className="visa-btn ghost-btn" onClick={handleBack}>
                ← Back to Overview
              </button>
              <button className="visa-btn" onClick={handleStep1Next}>
                Continue with {pricingInfo.category_name} →
              </button>
            </div>
          </div>
        )}

        {/* ─── STEP 2: Personal Information ──────────────────────────────────────── */}
        {step === 2 && (
          <div className="visa-personal-step">
            <div className="step-badge-row">
              <span className="active-cat-badge">{pricingInfo.category_name}</span>
              <span className="active-fee-badge">Fee: ${pricingInfo.total_fee_usd} USD (Rs. {pricingInfo.total_fee_lkr.toLocaleString()})</span>
            </div>

            <h3>Applicant Personal Information</h3>

            {/* Restricted Sponsor Route Notice */}
            {pricingInfo.is_restricted_sponsor && (
              <div className="restricted-warning-box">
                <FaExclamationTriangle className="icon" />
                <div>
                  <strong>Mandatory Local Sponsor Route (Battaramulla Head Office):</strong>
                  <p>
                    Applicants from this nationality must provide a verified Sri Lankan sponsor (Affidavit, Sponsor NIC/Passport, Police Clearance). Form fields for sponsor details are provided in subsequent steps.
                  </p>
                </div>
              </div>
            )}

            <label className="visa-label" htmlFor="fullName">
              Full Name (Exactly as shown in Passport) *
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              placeholder="e.g. John Alexander Smith"
              value={formData.fullName}
              onChange={handleChange}
            />
            {step2Errors.fullName && <p className="visa-field-error">{step2Errors.fullName}</p>}

            <div className="visa-date-row">
              <div>
                <label className="visa-label" htmlFor="dob">Date of Birth *</label>
                <input
                  type="date"
                  id="dob"
                  name="dob"
                  value={formData.dob}
                  onChange={handleChange}
                />
                {step2Errors.dob && <p className="visa-field-error">{step2Errors.dob}</p>}
              </div>

              <div>
                <label className="visa-label" htmlFor="gender">Gender *</label>
                <select id="gender" name="gender" value={formData.gender} onChange={handleChange}>
                  <option value="">Select Gender</option>
                  {genders.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
                {step2Errors.gender && <p className="visa-field-error">{step2Errors.gender}</p>}
              </div>
            </div>

            <label className="visa-label" htmlFor="nationality">Nationality / Passport Issuing Country *</label>
            <select
              id="nationality"
              name="nationality"
              value={formData.nationality}
              onChange={handleChange}
            >
              <option value="">Select Nationality</option>
              {nationalities.map((n) => (
                <option key={n.id} value={n.id}>{n.name}</option>
              ))}
            </select>
            {step2Errors.nationality && <p className="visa-field-error">{step2Errors.nationality}</p>}

            <label className="visa-label" htmlFor="passportNumber">Passport Number *</label>
            <input
              type="text"
              id="passportNumber"
              name="passportNumber"
              placeholder="e.g. A12345678"
              value={formData.passportNumber}
              onChange={handleChange}
            />
            {step2Errors.passportNumber && <p className="visa-field-error">{step2Errors.passportNumber}</p>}

            <div className="visa-date-row">
              <div>
                <label className="visa-label" htmlFor="passportIssue">Passport Issue Date *</label>
                <input
                  type="date"
                  id="passportIssue"
                  name="passportIssue"
                  value={formData.passportIssue}
                  onChange={handleChange}
                />
                {step2Errors.passportIssue && <p className="visa-field-error">{step2Errors.passportIssue}</p>}
              </div>

              <div>
                <label className="visa-label" htmlFor="passportExpiry">Passport Expiry Date (Min 6 months) *</label>
                <input
                  type="date"
                  id="passportExpiry"
                  name="passportExpiry"
                  value={formData.passportExpiry}
                  onChange={handleChange}
                />
                {step2Errors.passportExpiry && <p className="visa-field-error">{step2Errors.passportExpiry}</p>}
              </div>
            </div>

            <div className="visa-nav-buttons">
              <button className="visa-btn ghost-btn" onClick={handleBack}>
                ← Back
              </button>
              <button className="visa-btn" onClick={handleStep2Submit}>
                Next: Contact Info →
              </button>
            </div>
          </div>
        )}

        {/* ─── STEP 3: Contact Info ──────────────────────────────────────────────── */}
        {step === 3 && (
          <div className="visa-contact-step">
            <h3>Contact & Emergency Details</h3>
            <p className="step-subtitle">
              Your official ETA approval grant certificate will be emailed to this address.
            </p>

            <label className="visa-label" htmlFor="email">Email Address (For ETA Delivery) *</label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="e.g. traveler@example.com"
              value={formData.email}
              onChange={handleChange}
            />

            <label className="visa-label" htmlFor="phone">Phone / WhatsApp Number *</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              placeholder="e.g. +1 555 123 4567"
              value={formData.phone}
              onChange={handleChange}
            />

            <div className="visa-date-row">
              <div>
                <label className="visa-label" htmlFor="emergencyName">Emergency Contact Name</label>
                <input
                  type="text"
                  id="emergencyName"
                  name="emergencyName"
                  placeholder="e.g. Sarah Smith"
                  value={formData.emergencyName}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label className="visa-label" htmlFor="emergencyPhone">Emergency Contact Phone</label>
                <input
                  type="tel"
                  id="emergencyPhone"
                  name="emergencyPhone"
                  placeholder="e.g. +1 555 987 6543"
                  value={formData.emergencyPhone}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="visa-nav-buttons">
              <button className="visa-btn ghost-btn" onClick={handleBack}>
                ← Back
              </button>
              <button
                className="visa-btn"
                onClick={handleStep3Submit}
                disabled={!formData.email || !formData.phone}
              >
                Next: Travel Details →
              </button>
            </div>
          </div>
        )}

        {/* ─── STEP 4: Travel Details ────────────────────────────────────────────── */}
        {step === 4 && (
          <div className="visa-travel-step">
            <h3>Travel & Stay Information</h3>

            <label className="visa-label" htmlFor="purpose">Purpose of Visit</label>
            <input
              type="text"
              id="purpose"
              name="purpose"
              placeholder="e.g. Holiday, Sightseeing, Business Meetings"
              value={formData.purpose}
              onChange={handleChange}
            />

            <div className="visa-date-row">
              <div>
                <label className="visa-label" htmlFor="departureDate">Expected Arrival Date in Sri Lanka *</label>
                <input
                  type="date"
                  id="departureDate"
                  name="departureDate"
                  value={formData.departureDate}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label className="visa-label" htmlFor="returnDate">Expected Departure Date *</label>
                <input
                  type="date"
                  id="returnDate"
                  name="returnDate"
                  value={formData.returnDate}
                  onChange={handleChange}
                />
              </div>
            </div>

            <label className="visa-label" htmlFor="accommodation">Accommodation / Hotel Name & Address in Sri Lanka</label>
            <input
              type="text"
              id="accommodation"
              name="accommodation"
              placeholder="e.g. Cinnamon Grand Colombo, Galle Face Hotel"
              value={formData.accommodation}
              onChange={handleChange}
            />

            <div className="visa-date-row">
              <div>
                <label className="visa-label" htmlFor="destinations">Visiting Cities / Destinations</label>
                <input
                  type="text"
                  id="destinations"
                  name="destinations"
                  placeholder="e.g. Colombo, Kandy, Galle, Sigiriya"
                  value={formData.destinations}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label className="visa-label" htmlFor="flightNumber">Flight / Vessel Number</label>
                <input
                  type="text"
                  id="flightNumber"
                  name="flightNumber"
                  placeholder="e.g. UL 504, EK 652"
                  value={formData.flightNumber}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* If Restricted Sponsor Track -> Additional Sponsor Information Fields */}
            {pricingInfo.is_restricted_sponsor && (
              <div className="sponsor-form-section">
                <h4>Mandatory Local Sri Lankan Sponsor Details</h4>
                <div className="visa-date-row">
                  <div>
                    <label className="visa-label" htmlFor="sponsorName">Sponsor Full Name *</label>
                    <input
                      type="text"
                      id="sponsorName"
                      name="sponsorName"
                      placeholder="e.g. Priyantha Perera"
                      value={formData.sponsorName}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label className="visa-label" htmlFor="sponsorNic">Sponsor NIC / Passport *</label>
                    <input
                      type="text"
                      id="sponsorNic"
                      name="sponsorNic"
                      placeholder="e.g. 198512345678 or N1234567"
                      value={formData.sponsorNic}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="visa-date-row">
                  <div>
                    <label className="visa-label" htmlFor="sponsorPhone">Sponsor Phone *</label>
                    <input
                      type="tel"
                      id="sponsorPhone"
                      name="sponsorPhone"
                      placeholder="e.g. 077 123 4567"
                      value={formData.sponsorPhone}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label className="visa-label" htmlFor="sponsorAddress">Sponsor Sri Lankan Address *</label>
                    <input
                      type="text"
                      id="sponsorAddress"
                      name="sponsorAddress"
                      placeholder="e.g. 12/A, Baseline Road, Colombo 08"
                      value={formData.sponsorAddress}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="visa-nav-buttons">
              <button className="visa-btn ghost-btn" onClick={handleBack}>
                ← Back
              </button>
              <button className="visa-btn" onClick={handleStep4Submit}>
                Next: Upload Documents →
              </button>
            </div>
          </div>
        )}

        {/* ─── STEP 5: Document Upload ───────────────────────────────────────────── */}
        {step === 5 && (
          <div className="visa-documents-step">
            <h3>Required Document Uploads</h3>
            <p className="step-subtitle">
              Upload clear PDF or image copies. Minimum requirement: Passport Bio-Data page (must be valid for at least 6 months).
            </p>

            {pricingInfo.is_restricted_sponsor && (
              <div className="doc-instruction-box">
                <strong>Sponsor Route Required Documents:</strong>
                <ul>
                  <li>• Passport Bio-Data Page</li>
                  <li>• Local sponsor request letter</li>
                  <li>• Affidavit signed before JP / Lawyer</li>
                  <li>• Sponsor NIC / Passport copy</li>
                  <li>• Applicant Police Clearance Report</li>
                </ul>
              </div>
            )}

            {formData.visaType === "2" && (
              <div className="doc-instruction-box">
                <strong>Business ETA Requirement:</strong>
                <p>Please attach your Local Sri Lankan Corporate Invitation / Request Letter along with your passport bio page.</p>
              </div>
            )}

            <div className="upload-box">
              <input
                type="file"
                name="uploadedFiles"
                multiple
                onChange={handleChange}
                className="visa-file-input"
                id="docUploadInput"
              />
              <label htmlFor="docUploadInput" style={{ cursor: "pointer" }}>
                <FaPassport className="upload-icon" />
                <p><strong>Click to browse files</strong> or drag & drop documents here</p>
                <span style={{ fontSize: "13px", color: "#64748b" }}>Supported: PDF, JPG, PNG (Max 10MB each)</span>
              </label>
            </div>

            {formData.uploadedFiles && formData.uploadedFiles.length > 0 && (
              <div className="uploaded-files-list">
                <h4>Uploaded Files ({formData.uploadedFiles.length})</h4>
                <div className="file-chips">
                  {formData.uploadedFiles.map((file, i) => (
                    <div key={i} className="file-chip">
                      <FaFilePdf className="file-icon" /> {file.name}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Terms & Conditions */}
            <div className="visa-terms-card">
              <input
                type="checkbox"
                id="acceptTerms"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
              />
              <label htmlFor="acceptTerms">
                I hereby declare that all information supplied in this application is accurate. I understand that the ETA is valid across all international airports and designated ports in Sri Lanka for short visits, and that localized employment is strictly prohibited. I accept the{" "}
                <a href="/policies" target="_blank" rel="noreferrer">Terms and Conditions</a>.
              </label>
            </div>

            <div className="visa-nav-buttons">
              <button className="visa-btn ghost-btn" onClick={handleBack}>
                ← Back
              </button>
              <button
                className="visa-btn"
                onClick={handleDocumentUploadAndAcceptTerms}
                disabled={!acceptTerms || !formData.uploadedFiles?.length}
              >
                Proceed to Payment →
              </button>
            </div>
          </div>
        )}

        {/* ─── STEP 6: Transparent Payment & Secure Checkout ────────────────────── */}
        {step === 6 && (
          <div className="visa-payment-step">
            <h3 style={{ fontSize: "24px", fontWeight: "800", color: "#1e293b", marginBottom: "20px" }}>
              ETA Fee Summary & Secure Checkout
            </h3>

            <div className="visa-payment-grid">
              {/* Summary Side */}
              <div className="visa-payment-summary">
                <h4>Application Breakdown</h4>
                <ul>
                  <li>
                    <span>Application Reference</span>
                    <strong>ETA-{String(applicationId || "0000").padStart(6, "0")}</strong>
                  </li>
                  <li>
                    <span>Applicant Name</span>
                    <strong>{formData.fullName || "Applicant"}</strong>
                  </li>
                  <li>
                    <span>Passport Number</span>
                    <strong>{formData.passportNumber || "N/A"}</strong>
                  </li>
                  <li>
                    <span>ETA Category</span>
                    <strong>{pricingInfo.category_name}</strong>
                  </li>
                  <li>
                    <span>Standard Duration & Entry</span>
                    <strong>{pricingInfo.standard_duration} ({pricingInfo.entry_type})</strong>
                  </li>
                  <li>
                    <span>Sri Lanka Government Visa Fee</span>
                    <strong>${pricingInfo.govt_fee_usd.toFixed(2)} USD {pricingInfo.is_free_tier ? "(Free Tier Exemption)" : ""}</strong>
                  </li>
                  <li>
                    <span>SoloWay Verification & Platform Charge</span>
                    <strong style={{ color: "#0f6eb5" }}>$10.00 USD</strong>
                  </li>
                  <li className="total-row">
                    <span>Total Amount Payable (USD)</span>
                    <strong>${pricingInfo.total_fee_usd.toFixed(2)} USD</strong>
                  </li>
                  <li className="lkr-row">
                    <span>Total Amount Payable (LKR @ 300)</span>
                    <strong>Rs. {pricingInfo.total_fee_lkr.toLocaleString()}</strong>
                  </li>
                </ul>
              </div>

              {/* Payment Methods Side */}
              <div className="visa-payment-action">
                <label className="visa-label" style={{ fontSize: "16px", fontWeight: "700", color: "#1e293b" }}>
                  Select Secure Payment Method
                </label>

                <div 
                  className={`visa-payment-option ${isProcessingPayment ? "processing" : ""}`} 
                  onClick={!isProcessingPayment ? handlePaymentsLkVisaPayment : undefined}
                  style={{ cursor: isProcessingPayment ? "wait" : "pointer", opacity: isProcessingPayment ? 0.7 : 1 }}
                >
                  <div className="option-icon"><FaCreditCard /></div>
                  <div className="option-info">
                    <span className="option-title">
                      Payments.lk Hosted Checkout (Payable)
                      <span style={{ fontSize: "11px", background: "#e0f2fe", color: "#0284c7", padding: "2px 6px", borderRadius: "4px", marginLeft: "8px", fontWeight: "bold" }}>
                        Recommended
                      </span>
                    </span>
                    <span className="option-desc">Visa, MasterCard, AMEX, LankaPay, QR</span>
                  </div>
                  <div className="option-price">
                    <div className="option-price-amount">
                      {isProcessingPayment ? "Connecting..." : `Rs. ${pricingInfo.total_fee_lkr.toLocaleString()}`}
                    </div>
                    <div className="option-price-label">${pricingInfo.total_fee_usd} USD Total</div>
                  </div>
                </div>

                <div className="security-badge">
                  <FaShieldAlt /> 256-bit SSL Encrypted & Official Immigration Synchronized Processing
                </div>

                <div className="visa-nav-buttons" style={{ marginTop: "20px" }}>
                  <button className="visa-btn ghost-btn" onClick={handleBack}>
                    ← Back to Documents
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── STEP 7: Success Screen ────────────────────────────────────────────── */}
        {step === 7 && (
          <div className="visa-success-step advanced-success">
            <div className="success-card">
              <div className="success-icon">
                <FaCheckCircle size={80} color="#16a34a" />
              </div>

              <h2 className="success-title">ETA Application Submitted Successfully!</h2>
              <p className="success-message">
                Your Sri Lanka Electronic Travel Authorization application has been received and is being processed directly with the Department of Immigration & Emigration.
              </p>

              <div className="application-id">
                <span>ETA Reference Code:</span>
                <strong>ETA-{String(applicationId || "000000").padStart(6, "0")}</strong>
              </div>

              <div className="success-info-box">
                <p>• <strong>Processing Time:</strong> Typically within 24 hours.</p>
                <p>• <strong>Grant Voucher:</strong> Your official PDF certificate will be delivered to <strong>{formData.email || "your registered email"}</strong>.</p>
                <p>• <strong>Stay Duration:</strong> {pricingInfo.standard_duration} ({pricingInfo.entry_type}). Extendable up to 270 days in Colombo.</p>
              </div>

              <div className="success-actions">
                <button
                  className="visa-btn"
                  onClick={() => (window.location.href = "/")}
                >
                  Return to Home
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default VisaApplication;
